import type { ReactNode } from 'react';
import type { DashboardData, DashboardWidgetWidth, DashboardLayoutItem } from '@/types';
import * as W from './widgets';

/**
 * Rejestr widgetów dashboardu. Kolejność tej tablicy = domyślny układ (DEFAULT_LAYOUT).
 * `widths` = dozwolone szerokości przy przełączaniu w trybie edycji.
 *
 * `requireAnyPerm` ogranicza widget do posiadaczy CHOĆ JEDNEGO z wymienionych uprawnień
 * (gating przy renderze, nie w zapisie układu). Wcześniej stało tu `requireRoles` z zaszytymi
 * nazwami ról — jedyne takie miejsce w całym froncie i niezgodne z architekturą: role są
 * dynamiczne (katalog app_role, tworzone w UI), a źródłem prawdy jest macierz uprawnień.
 * Skutek był dwustronny: nowa rola „Kadrowa" z pełnym HR_VIEW nie zobaczyłaby widgetu kadr,
 * a rola nazwana przypadkiem OFFICE zobaczyłaby nazwiska pracowników bez żadnych uprawnień.
 *
 * UWAGA: to warstwa porządkowa, nie zabezpieczenie. Dane, które nie powinny wyciec, są
 * odcinane w DashboardApi — ukrycie widgetu jest widoczne w DevTools.
 */
export interface WidgetDef {
  key: string;
  title: string;
  defaultWidth: DashboardWidgetWidth;
  widths: DashboardWidgetWidth[];
  requireAnyPerm?: string[];
  render: (data: DashboardData) => ReactNode;
}

const KPI_WIDTHS: DashboardWidgetWidth[] = ['kpi', 'half'];
const CARD_WIDTHS: DashboardWidgetWidth[] = ['half', 'full'];

/**
 * Uprawnienia dobrane tak, żeby widget pokazywał się dokładnie tym, którzy mogą wejść na
 * odpowiadającą mu stronę (te same wartości co RequirePerm w App.tsx i requirePerm w menu).
 *
 * Widgety opracowań też wymagają STUDY_VIEW, mimo że ich DANE są bezpieczne (backend przepuszcza
 * je przez filtrujWidoczne, czyli dostęp per-opracowanie). Powód jest użytkowy, nie bezpieczeństwowy:
 * bez STUDY_VIEW trasa /studies i tak odmawia wejścia, więc kafelki pokazywały jedynie zera
 * i prowadziły w ekran „brak dostępu".
 */
export const WIDGETS: WidgetDef[] = [
  { key: 'kpi-active-intakes', title: 'Aktywne ujęcia (KPI)', defaultWidth: 'kpi', widths: KPI_WIDTHS, requireAnyPerm: ['INTAKE_VIEW'], render: d => <W.KpiActiveIntakes data={d}/> },
  { key: 'kpi-studies',        title: 'Opracowania w toku (KPI)', defaultWidth: 'kpi', widths: KPI_WIDTHS, requireAnyPerm: ['STUDY_VIEW'], render: d => <W.KpiStudies data={d}/> },
  { key: 'kpi-deadlines',      title: 'Terminy w tym tyg. (KPI)', defaultWidth: 'kpi', widths: KPI_WIDTHS, requireAnyPerm: ['STUDY_VIEW'], render: d => <W.KpiDeadlines data={d}/> },
  { key: 'kpi-ai-drafts',      title: 'Drafty AI (KPI)', defaultWidth: 'kpi', widths: KPI_WIDTHS, requireAnyPerm: ['AI_GENERATE', 'AI_VIEW_COSTS'], render: d => <W.KpiAiDrafts data={d}/> },

  { key: 'types-count',        title: 'Typy opracowań w toku', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['STUDY_VIEW'], render: d => <W.TypesCountWidget data={d}/> },
  { key: 'deadlines-alerts',   title: 'Terminy i alarmy', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['STUDY_VIEW'], render: d => <W.DeadlinesAlertsWidget data={d}/> },
  { key: 'top-intakes',        title: 'Najwydajniejsze ujęcia', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['INTAKE_VIEW'], render: d => <W.TopIntakesWidget data={d}/> },
  { key: 'compliance',         title: 'Terminy: przeglądy, BHP, ubezpieczenia', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['COMPLIANCE_VIEW'], render: d => <W.ComplianceWidget data={d}/> },
  { key: 'my-tasks',           title: 'Moje zadania na dziś', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['TASK_VIEW'], render: d => <W.MyTasksWidget data={d}/> },
  // HR_VIEW *lub* FLEET_VIEW — widget scala dwa niezależne moduły, a backend wypełnia każdą
  // sekcję osobno. Koniunkcja ukryłaby go geologowi, który ma dostęp do pojazdów, ale nie kadr.
  { key: 'hr-fleet',           title: 'Terminy: Kadry i Pojazdy', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['HR_VIEW', 'FLEET_VIEW'], render: d => <W.HrFleetWidget data={d}/> },
  { key: 'upcoming-trips',     title: 'Najbliższe wyjazdy brygad', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['CALENDAR_VIEW'], render: d => <W.UpcomingTripsWidget data={d}/> },
  { key: 'subscription-payments', title: 'Nadchodzące płatności (subskrypcje)', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['FINANCE_VIEW'], render: d => <W.SubscriptionPaymentsWidget data={d}/> },
  { key: 'ai-assistant',       title: 'Asystent AI', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['AI_GENERATE', 'AI_VIEW_COSTS'], render: d => <W.AiAssistantWidget data={d}/> },
  { key: 'rag-kb',             title: 'Baza wiedzy (RAG)', defaultWidth: 'half', widths: CARD_WIDTHS, requireAnyPerm: ['DRIVE_VIEW'], render: d => <W.RagKbWidget data={d}/> },
];

export const WIDGET_MAP: Record<string, WidgetDef> = Object.fromEntries(WIDGETS.map(w => [w.key, w]));

/** Czy widget o danym kluczu jest dostępny przy podanych uprawnieniach (gating przy renderze). */
export function widgetAllowedForPerms(key: string, permissions: string[] | undefined): boolean {
  const def = WIDGET_MAP[key];
  if (!def) return false;
  if (!def.requireAnyPerm) return true;
  return def.requireAnyPerm.some(p => (permissions ?? []).includes(p));
}

/** Domyślny układ = kolejność + defaultWidth z rejestru, wszystko widoczne. */
export const DEFAULT_LAYOUT: DashboardLayoutItem[] =
  WIDGETS.map(w => ({ key: w.key, visible: true, width: w.defaultWidth }));
